import type { ComponentProps, PropsWithChildren } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { backgroundColor as bg, display, left, maxHeight, position } from 'styled-system';
import type {
  BackgroundColorProps,
  DisplayProps,
  LeftProps,
  MaxHeightProps,
  PositionProps,
} from 'styled-system';

import { Flex } from 'common/components/Flex';
import type { FlexProps } from 'common/components/Flex';
import { GRID_MAX_WIDTH } from 'common/constants/grid';
import type { Color } from 'common/theme/colors/types';
import { boxSizing, overflowY } from 'common/theme/system-utilities';
import type { BoxSizingProps, OverflowYProps } from 'common/theme/system-utilities';
import { breakpoints } from 'common/theme/theme';
import { HALF_SIZE_VARIANT_TEXTBOX_WIDTH } from '../../../constants';
import type { BannerProps } from '../../../types';

interface DesktopTextBoxProps extends Partial<BannerProps>, PropsWithChildren {
  isTextMissing?: boolean;
}

const Container = styled(Flex)<
  FlexProps &
    BackgroundColorProps &
    BoxSizingProps &
    DisplayProps &
    PositionProps &
    MaxHeightProps &
    LeftProps &
    Pick<DesktopTextBoxProps, 'isViewportWide'>
>`
  ${bg};
  ${display};
  ${position};
  ${maxHeight};
  ${left};
  ${boxSizing};
  z-index: 1;

  /* Prevents the text box from overflowing past the image boundary (35vw)
     by accounting for the GridConstraintOverlay centering offset and padding */
  ${({ isViewportWide }) =>
    isViewportWide &&
    `
    max-width: calc(35vw - max(0px, (100vw - ${GRID_MAX_WIDTH}px) / 2) - 48px);

    @media (min-width: ${breakpoints[2]}) {
      max-width: calc(35vw - max(0px, (100vw - ${GRID_MAX_WIDTH}px) / 2) - 64px);
    }
  `}
`;

const containerStyles: ComponentProps<typeof Container> = {
  width: HALF_SIZE_VARIANT_TEXTBOX_WIDTH,
  pt: [null, null, 'sp_16', 'sp_32'],
  pr: [null, null, 'sp_16', 'sp_20'],
  pb: [null, null, 'sp_16', 'sp_32'],
  pl: [null, null, 'sp_24', 'sp_32'],
  boxSizing: 'border-box',
  display: ['none', null, 'flex'],
  position: 'absolute',
  maxHeight: '100%',
  left: 0,
};

const InnerContainer = styled(Flex)<FlexProps & BackgroundColorProps & OverflowYProps>`
  ${bg};
  ${display};
  ${overflowY};
`;

const innerContainerStyles: ComponentProps<typeof InnerContainer> = {
  width: '100%',
  overflowY: ['hidden', null, 'visible'],
  alignItems: 'flex-start',
};

const DesktopTextBox = ({
  children,
  backgroundColor,
  isViewportWide,
  isTextMissing,
}: DesktopTextBoxProps) => {
  if (isTextMissing) return null;

  return (
    <Container
      {...containerStyles}
      isViewportWide={isViewportWide}
      bg={(backgroundColor ?? 'white') as Color}
    >
      <InnerContainer {...innerContainerStyles}>{children}</InnerContainer>
    </Container>
  );
};

export default DesktopTextBox;
